import { COLORS } from './colors';
import { GRADIENTS } from './gradients';
import type { BaseThemeConfig } from './theme.types';
import type {
  BoldTypographyVariant,
  TypographyVariant,
  TypographyVariantConfig,
} from './themeComponents.types';

const LETTER_SPACING_NORMAL = 'normal';
const WEIGHT_REGULAR = 400;
const WEIGHT_SEMIBOLD = 600;
const TEXT_TRANSFORM_NONE = 'none';

const baseVariantConfig: Partial<TypographyVariantConfig> = {
  'h1-regular': {
    branded: true,
    desktopStyles: {
      fontSize: '36px',
      lineHeight: '40px',
    },
    fontSize: '28px',
    lineHeight: '32px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'h2-regular': {
    branded: true,
    desktopStyles: {
      fontSize: '24px',
      lineHeight: '32px',
    },
    fontSize: '24px',
    lineHeight: '28px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'h3-regular': {
    branded: true,
    desktopStyles: {
      fontSize: '20px',
      lineHeight: '24px',
    },
    fontSize: '20px',
    lineHeight: '24px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'h4-regular': {
    branded: true,
    desktopStyles: {
      fontSize: '18px',
      lineHeight: '24px',
    },
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'body-regular': {
    desktopStyles: {
      fontSize: '16px',
      lineHeight: '24px',
    },
    fontSize: '15px',
    lineHeight: '24px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'small-body-regular': {
    desktopStyles: {
      fontSize: '14px',
      lineHeight: '24px',
    },
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: LETTER_SPACING_NORMAL,
    textTransform: TEXT_TRANSFORM_NONE,
  },
  'caps-regular': {
    desktopStyles: {
      fontSize: '14px',
      lineHeight: '24px',
    },
    fontSize: '13px',
    lineHeight: '24px',
    fontWeight: WEIGHT_REGULAR,
    letterSpacing: '1.3px',
    textTransform: 'uppercase',
  },
};

const boldVariantConfig = Object.keys(baseVariantConfig)
  .map((value) => ({
    [value.replace('-regular', '-semibold')]: {
      ...baseVariantConfig[value as TypographyVariant],
      fontWeight: WEIGHT_SEMIBOLD,
    },
  }))
  .reduce((prev, curr) => ({
    ...prev,
    ...curr,
  })) as TypographyVariantConfig<BoldTypographyVariant>;

/**
 * All Reefer theme configuration that can be customized
 */
export const BASE_CONFIG: BaseThemeConfig = {
  borderRadius: {
    xs: '12px',
    sm: '16px',
    lg: '24px',
    xl: '36px',
  },

  colors: {
    primary: COLORS.brand.purple,
    secondary: COLORS.brand.gold,
    info: COLORS.brand.sunset,
    success: COLORS.system.positive,
    error: COLORS.system.negative,
    background: COLORS.grays.white,

    gradients: {
      primary: GRADIENTS.primary,
    },

    text: {
      main: COLORS.grays.black,
      inverse: COLORS.grays.white,
      info: COLORS.grays.white,
      light: COLORS.grays.mid,
    },
  },

  components: {
    Button: {
      icons: {
        sizes: {
          default: {
            height: '48px',
          },
          jumbo: {
            height: '48px',
          },
          large: {
            height: '48px',
          },
          small: {
            height: '48px',
          },
        },
      },
      shared: {
        borderRadius: 'lg',
        borderWidth: '2px',
        typographyVariant: 'body-semibold',
      },
      sizes: {
        default: {
          height: '48px',
          paddingX: 16,
        },
        jumbo: {
          height: '72px',
          paddingX: 16,
        },
        large: {
          height: '72px',
          paddingX: 16,
        },
        small: {
          height: '32px',
          paddingX: 16,
          typographyVariant: 'body-regular',
        },
      },
      variants: {
        destructive: {
          backgroundColor: 'error',
          borderColor: 'error',
          typographyColor: 'grays-white',
        },
        'destructive-secondary': {
          backgroundColor: 'error-light',
          borderColor: 'error-light',
          typographyColor: 'error-dark',
        },
        'gradient-primary': {
          backgroundColor: 'primary',
          borderColor: 'transparent',
          borderWidth: '0px',
          typographyColor: 'grays-white',
        },
        minimal: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          typographyColor: 'grays-black',
        },
        'minimal-inverse': {
          backgroundColor: 'grays-white',
          borderColor: 'grays-white',
          typographyColor: 'grays-black',
        },
        primary: {
          backgroundColor: 'primary',
          borderColor: 'primary',
          typographyColor: 'text-inverse',
        },
        'primary-inverse': {
          backgroundColor: 'grays-white',
          borderColor: 'grays-white',
          typographyColor: 'primary',
        },
        secondary: {
          backgroundColor: 'primary-light',
          borderColor: 'primary-light',
          typographyColor: 'primary',
        },
        tertiary: {
          backgroundColor: 'grays-ultralight',
          borderColor: 'grays-ultralight',
          typographyColor: 'grays-black',
        },
        'tertiary-selected': {
          backgroundColor: 'grays-ultralight',
          borderColor: 'primary',
          typographyColor: 'grays-black',
        },
      },
    },

    Card: {
      styles: {
        borderRadius: 'sm',
      },
    },

    Checkbox: {
      size: '24px',
      // Matches `size` so the hit area stays where it has always been. Themes
      // built for touch (Kiosk) grow it.
      tapTarget: '24px',
    },

    Icon: {
      sizes: {
        sm: '16px',
        md: '24px',
        lg: '32px',
        xl: '48px',
        xxl: '64px',
        xxxl: '96px',
      },
    },

    Typography: {
      componentMapping: {
        'h1-regular': 'h1',
        'h1-semibold': 'h1',
        'h2-regular': 'h2',
        'h2-semibold': 'h2',
        'h3-regular': 'h3',
        'h3-semibold': 'h3',
        'h4-regular': 'h4',
        'h4-semibold': 'h4',
        'body-regular': 'p',
        'body-semibold': 'p',
        'small-body-regular': 'p',
        'small-body-semibold': 'p',
        'caps-regular': 'p',
        'caps-semibold': 'p',
      },
      fonts: {
        branded: {
          fontFamily: 'Euclid Circular B',
        },
        default: {
          fontFamily: 'Jane Default',
        },
      },
      variants: {
        ...baseVariantConfig,
        ...boldVariantConfig,
      },
    },
  },
};
